import { useMemo } from "react";
//#region redux
//#endregion
//#region types
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { ReceiptFormTypes } from "models/formTypes";
import { TranslationKeys } from "translations";
import { FormFieldConstants } from "utils/constants";
import { DimensionEnums } from "models/enums";
import { UploadedFile } from "models/utilTypes/fileUtilsTypes";
import { ArraySchema } from "models/utilTypes/yupSchemaTypes";
//#endregion
//#region components
import { useDimensionMaps } from "components/common";
//#endregion
import * as Yup from "yup";
import { RegularExpressions } from "utils/helpers";

function useDetailsFormHelper() {
  const { dimensions, forcedDimensionMap } = useDimensionMaps(DimensionEnums.DimensionCategory.Receipt);

  const validationSchema = useMemo(
    () => ({
      createdBy: Yup.string(),
      receiptCategoryId: Yup.number(),
      receiptCategory: Yup.string(),
      persons: Yup.array()
        .of(
          Yup.object<ReceiptFormTypes.ReceiptPerson>({
            id: Yup.number(),
            name: Yup.string().required(TranslationKeys.Validations.Required),
            company: Yup.string().required(TranslationKeys.Validations.Required),
          })
        )
        .when(
          FormFieldConstants.Receipt.Details.ReceiptCategoryType,
          ([value]: [WintSuperkollAPIModel.ReceiptCategoryType], schema: Yup.Schema) =>
            ((value === WintSuperkollAPIModel.ReceiptCategoryType.Representation ||
              value === WintSuperkollAPIModel.ReceiptCategoryType.EmployeeRepresentation) &&
              schema.required(TranslationKeys.Validations.Required)) ||
            schema
        ),
      intent: Yup.string().when(
        FormFieldConstants.Receipt.Details.ReceiptCategoryType,
        ([value], schema: Yup.StringSchema) =>
          ((value === WintSuperkollAPIModel.ReceiptCategoryType.Representation ||
            value === WintSuperkollAPIModel.ReceiptCategoryType.EmployeeRepresentation) &&
            schema.required(TranslationKeys.Validations.Required)) ||
          schema
      ),
      otherExplanation: Yup.string().when(
        FormFieldConstants.Receipt.Details.ReceiptCategoryType,
        ([value], schema: Yup.StringSchema) =>
          (value === WintSuperkollAPIModel.ReceiptCategoryType.Other &&
            schema.required(TranslationKeys.Validations.Required)) ||
          schema
      ),
      otherPaymentMethodExplanation: Yup.string().when(
        FormFieldConstants.Receipt.Details.IsOtherPaymentMethod,
        ([isOtherPaymentMethod], schema: Yup.StringSchema) =>
          isOtherPaymentMethod
            ? schema
                .trim()
                .required(TranslationKeys.Validations.Required)
            : schema.notRequired()
      ),
      amount: Yup.number().when(
        [FormFieldConstants.Receipt.Details.PaymentMethodType],
        ([paymentMethod], schema: Yup.NumberSchema) =>
          paymentMethod !== WintSuperkollAPIModel.ReceiptPaymentMethodType.WintCard
            ? schema.required(TranslationKeys.Validations.Required).min(0.01, TranslationKeys.Validations.Required)
            : schema.required(TranslationKeys.Validations.Required)
      ),
      currency: Yup.string().required(TranslationKeys.Validations.Required),
      purchaseDate: Yup.date()
        .required(TranslationKeys.Validations.Required)
        .typeError(TranslationKeys.Validations.InvalidDate),
      paymentMethodId: Yup.number().required(TranslationKeys.Validations.Required),
      paymentMethod: Yup.string().required(TranslationKeys.Validations.Required),
      paymentStatus: Yup.string(),
      recipientAccount: Yup.string(),
      paymentDate: Yup.date().typeError(TranslationKeys.Validations.InvalidDate),
      dimensions: Yup.array().of(
        Yup.object<WintSuperkollAPIModel.DimensionData>({
          dimensionId: Yup.string(),
          amount: Yup.number(),
        })
      ),
      showDimensions: Yup.boolean(),
      filesToUpload: Yup.array().of(
        Yup.object<UploadedFile>({
          contentType: Yup.string(),
          fileName: Yup.string(),
          fileSize: Yup.number(),
          data: Yup.string(),
        })
      ),
      canPayEmployee: Yup.boolean(),
      isManualPayment: Yup.boolean(),
      personNumber: Yup.string()
        .matches(RegularExpressions.PersonNumber, {
          message: TranslationKeys.Validations.InvalidPersonNumber,
          excludeEmptyString: true,
        })
        .when(
          [
            FormFieldConstants.Receipt.Details.CanPayEmployee,
            FormFieldConstants.Receipt.Details.IsManualPayment,
            FormFieldConstants.Receipt.Details.PaymentMethodType,
          ],
          ([canPayEmployee, isManualPayment, paymentMethod], schema: Yup.StringSchema) =>
            !canPayEmployee &&
            paymentMethod === WintSuperkollAPIModel.ReceiptPaymentMethodType.OwnExpense &&
            !isManualPayment
              ? schema.required(TranslationKeys.Validations.Required)
              : schema
        ),
      bankAccount: Yup.string().when(
        [
          FormFieldConstants.Receipt.Details.CanPayEmployee,
          FormFieldConstants.Receipt.Details.IsManualPayment,
          FormFieldConstants.Receipt.Details.PaymentMethodType,
        ],
        ([canPayEmployee, isManualPayment, paymentMethod], schema: Yup.StringSchema) =>
          !canPayEmployee &&
          paymentMethod === WintSuperkollAPIModel.ReceiptPaymentMethodType.OwnExpense &&
          !isManualPayment
            ? schema.required(TranslationKeys.Validations.Required)
            : schema
      ),
      clearingNumber: Yup.string().when(
        [
          FormFieldConstants.Receipt.Details.CanPayEmployee,
          FormFieldConstants.Receipt.Details.IsManualPayment,
          FormFieldConstants.Receipt.Details.PaymentMethodType,
        ],
        ([canPayEmployee, isManualPayment, paymentMethod], schema: Yup.StringSchema) =>
          paymentMethod === WintSuperkollAPIModel.ReceiptPaymentMethodType.OwnExpense &&
          !canPayEmployee &&
          !isManualPayment
            ? schema.required(TranslationKeys.Validations.Required)
            : schema
      ),
      internalNote: Yup.string(),
    }),
    [dimensions, forcedDimensionMap]
  );

  const receiptDetailsValidationSchemeProperties = (isSoftValidation?: boolean) => {
    if (isSoftValidation) return validationSchema;

    return {
      ...validationSchema,
      receiptCategory: validationSchema.receiptCategory.required(TranslationKeys.Validations.Required),
      dimensions: validationSchema.dimensions
        .checkForcedDimensions(forcedDimensionMap)
        .when(
          FormFieldConstants.Receipt.Details.Amount,
          ([amount]: [number], schema: ArraySchema<WintSuperkollAPIModel.DimensionData>) =>
            schema.validateDimensionAmounts(dimensions, amount)
        ),
      filesToUpload: validationSchema.filesToUpload.required(TranslationKeys.Validations.Required),
    };
  };

  const receiptDetailsValidationScheme = (isSoftValidation?: boolean) => {
    return Yup.object<ReceiptFormTypes.Details>(receiptDetailsValidationSchemeProperties(isSoftValidation));
  };

  return { receiptDetailsValidationScheme, receiptDetailsValidationSchemeProperties };
}

export default useDetailsFormHelper;
